import React from "react";
import Pagination from "react-bootstrap/Pagination";
import "./style.css";

const CustomPagination = ({ currentPage, totalPages, onPageChange }) => {
	const safeTotalPages = Math.max(0, Number(totalPages) || 0);
	const safeCurrentPage = Math.min(
		Math.max(1, Number(currentPage) || 1),
		Math.max(1, safeTotalPages)
	);

	const goToPage = (page) => {
		if (!safeTotalPages) return;

		const nextPage = Math.min(Math.max(1, page), safeTotalPages);
		if (nextPage !== safeCurrentPage) {
			onPageChange(nextPage);
		}
	};

	const buildPageTokens = () => {
		if (safeTotalPages <= 1) return safeTotalPages === 1 ? [1] : [];

		if (safeTotalPages <= 5) {
			return Array.from({ length: safeTotalPages }, (_, index) => index + 1);
		}

		const visiblePages = new Set([1, safeTotalPages]);

		if (safeCurrentPage <= 2) {
			[1, 2, 3].forEach((page) => visiblePages.add(page));
		} else if (safeCurrentPage >= safeTotalPages - 1) {
			[safeTotalPages - 2, safeTotalPages - 1, safeTotalPages].forEach((page) =>
				visiblePages.add(page)
			);
		} else {
			[safeCurrentPage - 1, safeCurrentPage, safeCurrentPage + 1].forEach((page) =>
				visiblePages.add(page)
			);
		}

		const orderedPages = [...visiblePages]
			.filter((page) => page >= 1 && page <= safeTotalPages)
			.sort((a, b) => a - b);

		const tokens = [];
		orderedPages.forEach((page, index) => {
			if (index > 0) {
				const previousPage = orderedPages[index - 1];
				const gap = page - previousPage;

				if (gap === 2) {
					tokens.push(previousPage + 1);
				} else if (gap > 2) {
					tokens.push(`ellipsis-${previousPage}-${page}`);
				}
			}

			tokens.push(page);
		});

		return tokens;
	};

	if (!safeTotalPages) return null;

	return (
		<div className="custom-pagination-wrapper">
			<Pagination className="custom-pagination mb-0">
				<Pagination.First
					key="first"
					className="custom-pagination__edge"
					onClick={() => goToPage(1)}
					disabled={safeCurrentPage === 1}
				>
					<i className="fas fa-angle-double-left" />
				</Pagination.First>

				<Pagination.Prev
					key="prev"
					onClick={() => goToPage(safeCurrentPage - 1)}
					disabled={safeCurrentPage === 1}
				>
					<i className="fas fa-angle-left" />
				</Pagination.Prev>

				{buildPageTokens().map((token) => {
					if (typeof token === "string" && token.startsWith("ellipsis-")) {
						return (
							<Pagination.Ellipsis
								key={token}
								className="custom-pagination__ellipsis"
								disabled
							/>
						);
					}

					return (
						<Pagination.Item
							key={token}
							active={token === safeCurrentPage}
							onClick={() => goToPage(token)}
						>
							{token}
						</Pagination.Item>
					);
				})}

				<Pagination.Next
					key="next"
					onClick={() => goToPage(safeCurrentPage + 1)}
					disabled={safeCurrentPage === safeTotalPages}
				>
					<i className="fas fa-angle-right" />
				</Pagination.Next>

				<Pagination.Last
					key="last"
					className="custom-pagination__edge"
					onClick={() => goToPage(safeTotalPages)}
					disabled={safeCurrentPage === safeTotalPages}
				>
					<i className="fas fa-angle-double-right" />
				</Pagination.Last>
			</Pagination>
		</div>
	);
};

export default CustomPagination;
